/**
 * AITemplateGenerator Component
 *
 * Modal that lets a marketer describe what they want in natural language
 * and produces a draft WhatsApp template ready for review + submission to
 * Meta for approval.
 *
 * Today the generation is heuristic + a small lookup so we can ship the
 * UX end-to-end. The same surface will plug into a real LLM endpoint
 * (POST /api/v1/whatsapp/templates/generate) without UI changes.
 */

import { useState } from "react";
import {
  Sparkles,
  X,
  Loader2,
  Wand2,
  RefreshCw,
  ChevronRight,
} from "lucide-react";
import type {
  TemplateCategory,
  WhatsAppTemplate,
} from "../types";

interface AITemplateGeneratorProps {
  open: boolean;
  onClose: () => void;
  onAccept: (template: WhatsAppTemplate) => void;
}

const EXAMPLE_PROMPTS = [
  "Promo de 20% off para clientes inactivos hace 30 días",
  "Recordatorio de carrito abandonado con CTA al checkout",
  "Confirmación de pedido con tiempo estimado de entrega",
  "Felicitación de cumpleaños con regalo del 15%",
];

/**
 * Pseudo-LLM: maps prompt keywords to a realistic template body.
 * The real implementation calls the backend which calls OpenAI/Anthropic.
 */
function fakeGenerate(
  prompt: string,
  category: TemplateCategory,
): WhatsAppTemplate {
  const lower = prompt.toLowerCase();

  // Detect intent
  const isAbandoned = /carrito|abandon|olvid/.test(lower);
  const isPromo = /promo|descuento|off|oferta|%/.test(lower);
  const isBirthday = /cumple|nacimien|birthday/.test(lower);
  const isReactivation = /reactiv|inactiv|extra/.test(lower);
  const isConfirmation = /confirm|pedido|orden/.test(lower);

  // Extract a percentage if present
  const pctMatch = lower.match(/(\d{1,2})\s*%/);
  const pct = pctMatch ? pctMatch[1] : "{{2}}";

  let body: string;
  let footer: string | undefined;
  let buttons: WhatsAppTemplate["buttons"];
  let variableCount = 1; // always at least the name

  if (isAbandoned) {
    body =
      "Hola {{1}} 👋\n\nDejaste *{{2}} producto/s* en tu carrito. ¿Querés terminar tu compra?\n\nTotal: ${{3}}";
    variableCount = 3;
    buttons = [
      { type: "url", label: "Volver al carrito", url: "https://tienda.deenex.com/cart" },
      { type: "quick_reply", label: "Hablar con asesor" },
    ];
  } else if (isBirthday) {
    body = `🎂 ¡Feliz cumple, {{1}}!\n\nPara festejarlo, te regalamos *${pct}% OFF* en tu próxima compra.\n\nUsá el código *{{2}}* hasta el {{3}}.`;
    variableCount = 3;
    buttons = [
      { type: "url", label: "Usar regalo", url: "https://tienda.deenex.com/" },
    ];
  } else if (isReactivation) {
    body = `Hola {{1}}, te extrañamos 😢\n\nVolvé y disfrutá *${pct}% OFF* en tu próximo pedido.\n\nVálido por 7 días.`;
    variableCount = 1;
    buttons = [{ type: "quick_reply", label: "¡Me sumo!" }];
  } else if (isConfirmation) {
    body =
      "✅ Pedido confirmado\n\nHola {{1}}, tu pedido *#{{2}}* fue recibido.\nTotal: *${{3}}*\nLlega en aprox. {{4}} min.";
    variableCount = 4;
    buttons = [
      { type: "url", label: "Ver pedido", url: "https://tienda.deenex.com/orders/{{1}}" },
    ];
  } else if (isPromo) {
    body = `Hola {{1}}, tenemos una oferta especial para vos 🎉\n\n*${pct}% OFF* en toda la tienda usando el código *{{2}}*\n\nVálido hasta fin de mes.`;
    variableCount = 2;
    footer = "Si no querés recibir más promos, escribí BAJA.";
    buttons = [
      { type: "url", label: "Comprar ahora", url: "https://tienda.deenex.com/" },
      { type: "quick_reply", label: "Más info" },
    ];
  } else {
    // Generic fallback
    body = `Hola {{1}},\n\n${prompt.trim()}\n\nGracias por elegirnos.`;
    variableCount = 1;
  }

  return {
    id: `tpl-ai-${Date.now()}`,
    name: `ai_msg_${Date.now().toString(36).slice(-4)}`,
    category,
    language: "es_AR",
    approvalStatus: "approved", // Sin proceso Meta — todos quedan listos
    body,
    variableCount,
    footer,
    buttons,
    estimatedCostUSD: 0,
  };
}

export function AITemplateGenerator({
  open,
  onClose,
  onAccept,
}: AITemplateGeneratorProps) {
  const [prompt, setPrompt] = useState("");
  // category queda como dato interno (al pasar a QR Meta ya no aplica)
  const category: TemplateCategory = "marketing";
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState<WhatsAppTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError("Describí qué querés que diga el mensaje");
      return;
    }
    setError(null);
    setGenerating(true);
    // Simulate latency so the spinner reads as "AI is thinking"
    await new Promise((r) => setTimeout(r, 900));
    const result = fakeGenerate(prompt, category);
    setDraft(result);
    setGenerating(false);
  };

  const handleAccept = () => {
    if (!draft) return;
    onAccept(draft);
    setPrompt("");
    setDraft(null);
    onClose();
  };

  const handleClose = () => {
    setPrompt("");
    setDraft(null);
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-slate-900">
                Crear plantilla con IA
              </h3>
              <p className="text-xs text-slate-500">
                Describí qué querés y la IA propone una plantilla aprobable por
                Meta
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          {!draft ? (
            <div className="space-y-5">
              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  ¿Qué querés que diga el mensaje?
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Ej: Quiero recordarle al cliente que dejó productos en su carrito y ofrecerle un 10% off para que termine la compra."
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 resize-none
                    focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all duration-200"
                />
                {error && (
                  <p className="text-xs text-red-500 mt-2">{error}</p>
                )}
              </div>

              {/* Example prompts */}
              <div>
                <p className="text-xs text-slate-500 mb-2">
                  💡 Ejemplos para inspirarte:
                </p>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLE_PROMPTS.map((ex) => (
                    <button
                      key={ex}
                      onClick={() => setPrompt(ex)}
                      className="text-xs px-3 py-1.5 bg-slate-50 hover:bg-violet-50 hover:text-violet-700 border border-slate-100 hover:border-violet-200 rounded-full transition-all"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-violet-50 border border-violet-100 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 size={14} className="text-violet-600" />
                  <p className="text-xs font-semibold text-violet-700">
                    Plantilla generada
                  </p>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Revisala y aceptala. Cuando se envíe a Meta para aprobación
                  el estado pasará a "pendiente" hasta que la apruebe (24-48hs
                  según rubro).
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-xs text-slate-500">
                    {draft.name}
                  </span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                    Pendiente de aprobación
                  </span>
                </div>

                <div className="bg-white rounded-lg p-4 border border-slate-100 shadow-sm whitespace-pre-wrap text-sm text-slate-800 leading-snug">
                  {draft.body}
                </div>

                {draft.footer && (
                  <p className="mt-2 text-xs text-slate-400 px-1">
                    {draft.footer}
                  </p>
                )}

                {draft.buttons && draft.buttons.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {draft.buttons.map((btn, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-emerald-600 flex items-center gap-1.5"
                      >
                        <ChevronRight size={11} />
                        {btn.label}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-slate-200 flex items-center gap-4 text-xs text-slate-500">
                  <span>
                    Variables:{" "}
                    <span className="font-mono text-slate-700">
                      {draft.variableCount}
                    </span>
                  </span>
                  <span>
                    Idioma:{" "}
                    <span className="font-mono text-slate-700">
                      {draft.language}
                    </span>
                  </span>
                  <span className="ml-auto">
                    ${draft.estimatedCostUSD.toFixed(4)} / conv
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 flex-shrink-0">
          {!draft ? (
            <>
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 text-white
                  ${
                    generating || !prompt.trim()
                      ? "bg-violet-300 cursor-not-allowed"
                      : "bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 shadow-lg shadow-violet-500/25 active:scale-[0.98]"
                  }`}
              >
                {generating ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles size={15} />
                    Generar plantilla
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setDraft(null)}
                className="px-4 py-2.5 rounded-xl font-medium text-sm bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2"
              >
                <RefreshCw size={13} />
                Regenerar
              </button>
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
              >
                Descartar
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-500/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                Usar esta plantilla
                <ChevronRight size={15} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AITemplateGenerator;
